import { FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { SelectWithLabel } from '@components';
import {
  AppAccessType,
  AppAccess,
  DashboardSelectorSubmoduleItem,
  DEMO_ACCESS_TEMPLATE_TYPE,
} from '../types/dashboardSelector';
import { getDemoGameFilterId } from '../utils/utils';
import { useDashboardSelector } from '../hooks/useDashboardSelector';
import { useCustomAppsSidepanel } from '../hooks/useCustomAppsSidepanel';
import { useInviteUsers } from '../hooks/useInviteUsers';
import { useEditUser } from '../hooks/useEditUser';
import DashboardSelector from './DashboardSelector';
import FormPageLayout from './FormPageLayout';
import UserDetailsForm from './UserDetailsForm';
import { useUserDetailsForm } from '../hooks/useUserDetailsForm';
import SelectCustomAppsSidepanel from './AppAccessSidepanel/SelectCustomAppsSidepanel';
import { CUSTOM_TEMPLATE } from '../constants';
import { formFieldContainerSx, formSelectFieldStyles } from '../styles/formFieldStyles';
import { titles } from '@utils/translations/en';

const shouldSwitchToCustomTemplate = (
  previous: DashboardSelectorSubmoduleItem[],
  next: DashboardSelectorSubmoduleItem[],
  templateType: string,
): boolean => {
  if (templateType === CUSTOM_TEMPLATE.internalUserAccessTemplateType) {
    return false;
  }

  const previousIds = new Set(previous.map((s) => s.submoduleId));
  const nextIds = new Set(next.map((s) => s.submoduleId));
  const hasSelectedSubmodulesChanged =
    previousIds.size !== nextIds.size || [...previousIds].some((id) => !nextIds.has(id));

  if (hasSelectedSubmodulesChanged) {
    return true;
  }

  if (templateType === DEMO_ACCESS_TEMPLATE_TYPE) {
    return next.some((s) => s.appAccess?.type && s.appAccess.type !== AppAccessType.DEMO);
  }

  return false;
};

export type PvxUserFormMode = 'add' | 'edit' | 'clone';

interface AddPvxUserViewProps {
  mode?: PvxUserFormMode;
  userId?: string;
  onBack: () => void;
}

const AddPvxUserView: FC<AddPvxUserViewProps> = ({ mode = 'add', userId, onBack }) => {
  //Selected submodules
  const [selectedSubmodules, setSelectedSubmodules] = useState<DashboardSelectorSubmoduleItem[]>(
    [],
  );

  const { filterRegistry, refreshSubmoduleFilters } = useDashboardSelector({
    includePermissionSubmodules: true,
  });

  // Invite users
  const {
    saveStatuses,
    isSaving: isInviting,
    hasSaveAttempted,
    handleSave: inviteUsers,
    handleRetry: retryInviteUser,
  } = useInviteUsers();

  //Edit user
  const {
    userData,
    initialSubmodules,
    isLoading: isLoadingUser,
    isSaving: isUpdating,
    handleUpdate: updateUser,
  } = useEditUser(userId ?? '');

  const isEditMode = mode === 'edit';
  const isCloneMode = mode === 'clone';

  //User details form
  const {
    //Company
    selectedCompanyId,
    setSelectedCompanyId,
    companies,

    //User form
    userFormRows,
    areAllRowsValid,
    rowTouchedFields,
    rowFormErrors,
    getRoleOptions,
    handleFieldChange,
    handleFieldBlur,
    handleAddUserFormRow,
    handleRemoveUserFormRow,
    validateAll,

    //User template
    templateMenuItems,
    internalUserAccessTemplateType,
    templateSubmodules,
    isLoadingTemplates,
    setInternalUserAccessTemplateType,
  } = useUserDetailsForm(isEditMode, userData);

  const isDemoTemplate = internalUserAccessTemplateType === DEMO_ACCESS_TEMPLATE_TYPE;

  const isSaving = isEditMode ? isUpdating : isInviting;

  const isAdmin =
    userFormRows.every((row) => row.userRole === 'ADMIN') || userData?.userRole === 'ADMIN';

  const isSaveDisabled =
    isSaving ||
    !areAllRowsValid ||
    (!isAdmin && selectedSubmodules.length === 0) ||
    (!isEditMode && hasSaveAttempted);

  // Seed selectedSubmodules once edit-mode module access is fetched
  useEffect(() => {
    if (initialSubmodules !== undefined) {
      setSelectedSubmodules(initialSubmodules);
    }
  }, [initialSubmodules]);

  const setSelectedTemplateSubmodules = useCallback(() => {
    if (templateSubmodules) {
      const selectedTemplateSubmodules: DashboardSelectorSubmoduleItem[] = templateSubmodules.map(
        (s) => {
          const demoGameFilterId = getDemoGameFilterId(filterRegistry?.get(s.submoduleId) ?? {});
          const demoAppAccess: AppAccess = {
            type: AppAccessType.DEMO,
            filters: demoGameFilterId ? [demoGameFilterId] : undefined,
          };
          return {
            submoduleId: s.submoduleId,
            name: s.submoduleName,
            ...(isDemoTemplate ? { appAccess: demoAppAccess } : {}),
          };
        },
      );
      setSelectedSubmodules(selectedTemplateSubmodules);
    }
  }, [templateSubmodules, filterRegistry, isDemoTemplate]);

  useEffect(() => {
    setSelectedTemplateSubmodules();
  }, [internalUserAccessTemplateType]);

  const handleSaveClick = async () => {
    if (!validateAll()) return;
    if (isEditMode && userId) {
      const row = userFormRows[0];
      const success = await updateUser({
        userId,
        email: row.email,
        username: row.username,
        userRole: row.userRole,
        companyId: selectedCompanyId,
        internalUserAccessTemplateType,
        selectedSubmodules,
      });
      if (success) onBack();
    } else {
      inviteUsers({
        rows: userFormRows,
        companyId: selectedCompanyId,
        internalUserAccessTemplateType,
        selectedSubmodules,
      });
    }
  };

  const handleRetryUserInvite = async (index: number, retryId: string) => {
    await retryInviteUser({
      index,
      retryId,
      row: userFormRows[index],
      companyId: selectedCompanyId,
      internalUserAccessTemplateType,
      selectedSubmodules,
    });
  };

  const handleSelectedSubmodulesChange = useCallback(
    (submodules: SetStateAction<DashboardSelectorSubmoduleItem[]>) => {
      setSelectedSubmodules((previous) => {
        const next = typeof submodules === 'function' ? submodules(previous) : submodules;

        if (shouldSwitchToCustomTemplate(previous, next, internalUserAccessTemplateType)) {
          setInternalUserAccessTemplateType(CUSTOM_TEMPLATE.internalUserAccessTemplateType);
        }

        return next;
      });
    },
    [internalUserAccessTemplateType, setInternalUserAccessTemplateType],
  );

  const updateSubmoduleAppAccess = useCallback(
    (
      submodule: DashboardSelectorSubmoduleItem,
      appAccess: DashboardSelectorSubmoduleItem['appAccess'],
    ) => {
      setSelectedSubmodules((prev) => {
        const idx = prev.findIndex((s) => s.submoduleId === submodule.submoduleId);
        if (idx === -1) return [...prev, { ...submodule, appAccess }];
        return prev.map((s) => (s.submoduleId === submodule.submoduleId ? { ...s, appAccess } : s));
      });
    },
    [],
  );

  const {
    isOpen,
    activeSubmodule,
    filterMap,
    handleSidepanelSave,
    handleRefresh,
    handleSidepanelOpen,
    handleSidepanelClose,
  } = useCustomAppsSidepanel({
    onUpdateAppAccess: updateSubmoduleAppAccess,
    filterRegistry,
    onRefreshSubmoduleFilters: refreshSubmoduleFilters,
  });

  const isLoadingPage =
    (isEditMode || isCloneMode) && (isLoadingUser || initialSubmodules === undefined);

  return (
    <FormPageLayout
      title={isEditMode ? titles.EDIT_USER : titles.NEW_USER}
      onBack={onBack}
      onSave={handleSaveClick}
      onCancel={onBack}
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
    >
      {isLoadingPage ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#162936', mb: '1rem' }}>
            User Details
          </Typography>

          {/* Company selector */}
          <Box sx={{ mb: '1.5rem', ...formFieldContainerSx }}>
            <SelectWithLabel
              enableSearch
              label="Company"
              required
              labelId="company-select-label"
              selectStyles={formSelectFieldStyles}
              menuItems={companies.companyList.map((c) => ({
                label: `${c.name} [${c.clientCode}]`,
                value: c.id,
              }))}
              value={selectedCompanyId}
              onChange={(value) => setSelectedCompanyId(value)}
              isAddNew={true}
              disabled={!isEditMode && hasSaveAttempted}
            />
          </Box>

          {/* User details rows */}
          <UserDetailsForm
            rows={userFormRows}
            saveStatuses={saveStatuses}
            isSaving={isSaving}
            hasSaveAttempted={hasSaveAttempted}
            rowTouchedFields={rowTouchedFields}
            rowFormErrors={rowFormErrors}
            isEditMode={isEditMode}
            getRoleOptions={getRoleOptions}
            onFieldChange={handleFieldChange}
            onFieldBlur={handleFieldBlur}
            onAddRow={handleAddUserFormRow}
            onRemoveRow={handleRemoveUserFormRow}
            onRetry={handleRetryUserInvite}
          />

          {/* User Type (access template) */}
          <Box sx={{ mb: '1.5rem', ...formFieldContainerSx }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#162936', mb: '1rem' }}>
              User Type
            </Typography>
            <SelectWithLabel
              label=""
              labelId="user-type-select-label"
              menuItems={templateMenuItems}
              value={internalUserAccessTemplateType}
              onChange={(value) => setInternalUserAccessTemplateType(value)}
              disabled={isLoadingTemplates || (!isEditMode && hasSaveAttempted) || isAdmin}
              selectStyles={formSelectFieldStyles}
            />
          </Box>

          {/* Dashboard selector */}
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#162936', mb: '1rem' }}>
            {titles.SELECT_DASHBOARDS}
          </Typography>

          <DashboardSelector
            key={internalUserAccessTemplateType}
            isViewOnly={isAdmin}
            selectedSubmodules={selectedSubmodules}
            onSelectedSubmodulesChange={handleSelectedSubmodulesChange}
            filterRegistry={filterRegistry}
            onRefreshSubmoduleFilters={refreshSubmoduleFilters}
            onCustomAccessRequest={handleSidepanelOpen}
            includePermissionSubmodules={true}
          />

          <SelectCustomAppsSidepanel
            open={isOpen}
            moduleName={activeSubmodule?.name ?? ''}
            moduleId={activeSubmodule?.moduleId ?? ''}
            subModuleId={activeSubmodule?.submoduleId ?? ''}
            selectedFilters={activeSubmodule?.appAccess?.filters?.map((f) => f.rowId) ?? []}
            filterMap={filterMap}
            onRefreshFilters={handleRefresh}
            onSave={handleSidepanelSave}
            onClose={handleSidepanelClose}
            isViewOnly={isAdmin}
          />
        </>
      )}
    </FormPageLayout>
  );
};

export default AddPvxUserView;
